import {
  Box,
  Link,
  ResponsiveSection,
  Stack,
  Text,
} from '@stitch-fix/mode-react';
import type { GetStaticProps, NextPage } from 'next';
import {
  CareersHero,
  type CareersHeroProps,
} from '../../components/CareersHero/CareersHero';
import {
  ContentGallery,
  type ContentGalleryProps,
} from '../../components/ContentGallery';
import {
  FlexBlurbs,
  type FlexBlurbsProps,
} from '../../components/FlexBlurbs/FlexBlurbs';
import {
  FollowUs,
  type FollowUsProps,
} from '../../components/FollowUs/FollowUs';
import {
  FullWidthContent,
  type FullWidthContentProps,
} from '../../components/FullWidthContent/FullWidthContent';
import {
  JoinUsCta,
  type JoinUsCtaProps,
} from '../../components/JoinUsCta/JoinUsCta';
import { Layout } from '../../components/Layout';
import { Quotes, type QuotesProps } from '../../components/Quotes/Quotes';
import { SEOData, type SEODataProps } from '../../components/SEOData';
import {
  TripleBlock,
  type TripleBlockProps,
} from '../../components/TripleBlock';
import { serverLogger } from '../../utils/loggers/server';
import { getCachedEntry } from '../../utils/requests/contentstack/cachedRequests';
import { careersSchema } from './schema';

export type CareersPageProps = {
  seoData: SEODataProps;
  hero: CareersHeroProps;
  fullWidthContent: FullWidthContentProps;
  tripleBlock: TripleBlockProps;
  contentGallery: ContentGalleryProps;
  quotes: QuotesProps;
  totalRewards: FlexBlurbsProps;
  joinUs: JoinUsCtaProps;
  followUs: FollowUsProps;
  applicantPrivacyPolicy: {
    text: string;
    link: {
      text: string;
      href: string;
    };
  };
};

export const getStaticProps: GetStaticProps<CareersPageProps> = async () => {
  try {
    const entry = await getCachedEntry({
      contentType: 'careers',
      schema: careersSchema,
      logger: serverLogger,
    });

    return {
      props: entry.data,
      // revalidate configures the cache policy, this sets it to cache for 5 minutes
      // https://nextjs.org/docs/pages/building-your-application/deploying/production-checklist#caching
      revalidate: 300,
    };
  } catch (error: unknown) {
    serverLogger({
      level: 'error',
      team: 'client-onboarding',
      message: 'Failed to fetch required page data - /careers',
      context: {
        error,
        page: '/careers',
        severity: 'critical',
      },
    });

    throw new Error('Required data not provided to render /careers page');
  }
};

const CareersPage: NextPage<CareersPageProps> = props => {
  return (
    <Layout viewEventTrackingName="corporate">
      <ResponsiveSection mt="0" width={{ sm: 'full-bleed' }} gutter="none">
        <SEOData {...props.seoData} />
        <Stack
          mb={{ sm: '3rem', lg: '4rem' }}
          spacing={{ sm: '3rem', lg: '4rem' }}
        >
          <CareersHero {...props.hero} />
          <Box mt={{ sm: '-3rem', lg: '-4rem' }}>
            <FullWidthContent {...props.fullWidthContent} />
          </Box>
          <TripleBlock {...props.tripleBlock} />
          <ContentGallery {...props.contentGallery} />
          <Quotes {...props.quotes} />
          <FlexBlurbs {...props.totalRewards} />
          <Box mt={{ sm: '-3rem', lg: '-4rem' }}>
            <JoinUsCta {...props.joinUs} />
          </Box>
          <FollowUs {...props.followUs} />
          <Text mx={0.5} setting="body-small" align="center" spacingBottom>
            {props.applicantPrivacyPolicy.text}
            <br />
            <Link href={props.applicantPrivacyPolicy.link.href}>
              {props.applicantPrivacyPolicy.link.text}
            </Link>
          </Text>
        </Stack>
      </ResponsiveSection>
    </Layout>
  );
};

export default CareersPage;
